import { Injectable, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { DateAdapter } from '@angular/material/core';
import { getRegionLocale } from '../../utils/language.utils';

/**
 * Service responsible for managing regional locale state and synchronizing
 * internationalization (ngx-translate) with Angular Material components (DateAdapter).
 */
@Injectable({
  providedIn: 'root'
})
export class LocaleManagerService {
  private readonly translateService = inject(TranslateService);
  private readonly dateAdapter: DateAdapter<Date> = inject(DateAdapter);

  /**
   * Signal representation of language change events emitted by TranslateService.
   */
  private readonly langChange = toSignal(this.translateService.onLangChange, {
    initialValue: {
      lang: this.translateService.currentLang || this.translateService.defaultLang
    } as LangChangeEvent
  });

  /**
   * Computed signal providing the current region-specific locale code (e.g., 'es-GT', 'en-US').
   */
  readonly currentLocale = computed(() => {
    const lang = this.langChange()?.lang;
    return lang ? getRegionLocale(lang) : 'en-US';
  });

  constructor() {
    /**
     * Synchronizes Angular Material's DateAdapter locale whenever currentLocale updates.
     */
    effect(() => {
      this.dateAdapter.setLocale(this.currentLocale());
    });
  }
}
